import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import MapView from '../components/MapView';
import FlagImg from '../components/FlagImg';
import { IconLock, IconSparkles, IconGlobe, IconBarChart, IconMic } from '../components/Icons';
import '../styles/Dashboard.css';

// Shared live counter — same logic as AuthPage
const _BASE   = 20_000_000;
const _EPOCH  = new Date('2024-01-01T00:00:00Z').getTime();
const _RATE   = 2 / 1000;
function useLiveVoices() {
  const t0     = useRef(Date.now());
  const offset = Math.floor((Date.now() - _EPOCH) * _RATE);
  const [n, setN] = useState(_BASE + offset);
  useEffect(() => {
    const id = setInterval(() => {
      setN(_BASE + offset + Math.floor((Date.now() - t0.current) * _RATE));
    }, 800);
    return () => clearInterval(id);
  }, [offset]);
  return n;
}
function fmtM(n) {
  return (n / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const COUNTRY_NAMES = {
  AF:'Afghanistan',AL:'Albania',DZ:'Algeria',AO:'Angola',AR:'Argentina',AM:'Armenia',
  AU:'Australia',AT:'Austria',AZ:'Azerbaijan',BD:'Bangladesh',BY:'Belarus',BE:'Belgium',
  BJ:'Benin',BO:'Bolivia',BA:'Bosnia',BW:'Botswana',BR:'Brazil',BG:'Bulgaria',
  BF:'Burkina Faso',BI:'Burundi',KH:'Cambodia',CM:'Cameroon',CA:'Canada',CF:'Central African Rep.',
  TD:'Chad',CL:'Chile',CN:'China',CO:'Colombia',CG:'Congo',CD:'DR Congo',CR:'Costa Rica',
  CI:"Côte d'Ivoire",HR:'Croatia',CU:'Cuba',CY:'Cyprus',CZ:'Czechia',DK:'Denmark',
  DJ:'Djibouti',DO:'Dominican Rep.',EC:'Ecuador',EG:'Egypt',SV:'El Salvador',GQ:'Equatorial Guinea',
  ER:'Eritrea',EE:'Estonia',ET:'Ethiopia',FI:'Finland',FR:'France',GA:'Gabon',GM:'Gambia',
  GE:'Georgia',DE:'Germany',GH:'Ghana',GR:'Greece',GT:'Guatemala',GN:'Guinea',GW:'Guinea-Bissau',
  HT:'Haiti',HN:'Honduras',HU:'Hungary',IN:'India',ID:'Indonesia',IR:'Iran',IQ:'Iraq',
  IE:'Ireland',IL:'Israel',IT:'Italy',JM:'Jamaica',JP:'Japan',JO:'Jordan',KZ:'Kazakhstan',
  KE:'Kenya',KP:'North Korea',KR:'South Korea',KW:'Kuwait',KG:'Kyrgyzstan',LA:'Laos',
  LV:'Latvia',LB:'Lebanon',LS:'Lesotho',LR:'Liberia',LY:'Libya',LT:'Lithuania',
  MG:'Madagascar',MW:'Malawi',MY:'Malaysia',ML:'Mali',MR:'Mauritania',MX:'Mexico',
  MD:'Moldova',MN:'Mongolia',MA:'Morocco',MZ:'Mozambique',MM:'Myanmar',NA:'Namibia',
  NP:'Nepal',NL:'Netherlands',NZ:'New Zealand',NI:'Nicaragua',NE:'Niger',NG:'Nigeria',
  MK:'North Macedonia',NO:'Norway',OM:'Oman',PK:'Pakistan',PA:'Panama',PG:'Papua New Guinea',
  PY:'Paraguay',PE:'Peru',PH:'Philippines',PL:'Poland',PT:'Portugal',QA:'Qatar',
  RO:'Romania',RU:'Russia',RW:'Rwanda',SA:'Saudi Arabia',SN:'Senegal',RS:'Serbia',
  SL:'Sierra Leone',SO:'Somalia',ZA:'South Africa',SS:'South Sudan',ES:'Spain',LK:'Sri Lanka',
  SD:'Sudan',SR:'Suriname',SZ:'Eswatini',SE:'Sweden',CH:'Switzerland',SY:'Syria',
  TW:'Taiwan',TJ:'Tajikistan',TZ:'Tanzania',TH:'Thailand',TL:'Timor-Leste',TG:'Togo',
  TN:'Tunisia',TR:'Turkey',TM:'Turkmenistan',UG:'Uganda',UA:'Ukraine',AE:'UAE',
  GB:'United Kingdom',US:'United States',UY:'Uruguay',UZ:'Uzbekistan',VE:'Venezuela',
  VN:'Vietnam',YE:'Yemen',ZM:'Zambia',ZW:'Zimbabwe',
};

const DEMO_EVENTS = [
  {
    id: 'demo-drc-ireland',
    source: 'demo',
    title: 'Congolese asylum seekers face prolonged delays at Dublin processing centres',
    description: 'Families who fled violence in eastern DRC are waiting over 18 months for decisions, with limited legal aid and inadequate accommodation at international protection offices.',
    country_code: 'IE',
    affected_country_code: 'CD',
    region: 'Dublin, Ireland',
    source_type: 'refugee monitor',
    status: 'active',
    opinions_count: 156,
    is_featured: true,
    created_at: '2026-06-03T09:00:00Z',
    analytics: {
      summary: 'Contributors from the Congolese diaspora and Irish civil society are aligned on faster processing, independent legal representation, and humane accommodation standards while decisions are pending.',
      emotional_landscape: { anger: 38, sadness: 29, solidarity: 18, fear: 10, hope: 5 },
      key_themes: [
        { title: 'Faster decision timelines', percentage: 82 },
        { title: 'Independent legal representation', percentage: 71 },
        { title: 'Safe family accommodation', percentage: 64 },
      ],
      timeline: [22, 31, 40, 55, 68, 78],
      metrics: { voices: 156, countries: 11, consensus: 82, updateLabel: '2 hours ago' },
      geographic_insights: {
        key_insights: [
          'Congolese diaspora in Belgium and France highlight how DRC conflict context is often misunderstood in EU processing systems.',
          'Irish contributors focus on due-process guarantees and the cost of prolonged uncertainty on children.',
          'UK respondents draw parallels to the Windrush case and argue for systemic reform over case-by-case fixes.',
        ],
        diaspora_perspectives: 'Congolese contributors emphasize that flight from armed conflict in Kivu is well-documented and deserves expedited humanitarian consideration.',
        regional_patterns: 'Western European voices converge on independent legal aid and a binding 6-month decision deadline as the minimum standard.',
        active_countries: ['IE', 'CD', 'BE', 'FR', 'GB', 'US'],
      },
    },
  },
  {
    id: 'demo-ghana-port',
    source: 'demo',
    title: 'Port workers block fuel exports after wage freeze',
    description: 'Dockworkers in Tema are demanding a binding wage review and safer loading schedules after a six-month pay freeze.',
    country_code: 'GH',
    region: 'Tema, Ghana',
    source_type: 'labor bulletin',
    status: 'active',
    opinions_count: 184,
    is_featured: true,
    created_at: '2026-05-30T08:00:00Z',
    analytics: {
      summary: 'Contributors are pushing for a negotiated settlement that protects worker safety while keeping regional fuel supplies stable.',
      emotional_landscape: { anger: 34, sadness: 12, solidarity: 24, fear: 11, hope: 19 },
      key_themes: [
        { title: 'Independent wage review', percentage: 76 },
        { title: 'Safer shift rotations', percentage: 63 },
        { title: 'No retaliation against organizers', percentage: 52 },
      ],
      timeline: [36, 44, 51, 58, 67, 73],
      metrics: { voices: 184, countries: 9, consensus: 76, updateLabel: '5 hours ago' },
      geographic_insights: {
        key_insights: [
          'Diaspora contributors in London and Berlin are amplifying calls for labor mediation.',
          'Most Ghana-based voices support a short disruption if it produces a signed agreement.',
          'Regional commenters are focused on supply-chain stability for West African neighbors.',
        ],
        diaspora_perspectives: 'Ghanaian contributors abroad frame the strike as a test of whether essential workers can win basic protections without escalation.',
        regional_patterns: 'West African respondents want a rapid settlement and clear public communication on fuel availability.',
        active_countries: ['GH', 'GB', 'DE', 'NG', 'US'],
      },
    },
  },
  {
    id: 'demo-ireland-housing',
    source: 'demo',
    title: 'Dublin housing vigil grows into nightly accountability forum',
    description: 'Community groups are holding public vigils outside city offices to demand emergency housing timelines and tenant protection.',
    country_code: 'IE',
    region: 'Dublin, Ireland',
    source_type: 'community report',
    status: 'active',
    opinions_count: 129,
    is_featured: true,
    created_at: '2026-05-29T18:30:00Z',
    analytics: {
      summary: 'The conversation has moved from symbolic protest to practical demands around shelter access, emergency procurement, and transparent decision making.',
      emotional_landscape: { anger: 22, sadness: 21, solidarity: 28, fear: 8, hope: 21 },
      key_themes: [
        { title: 'Emergency housing targets', percentage: 72 },
        { title: 'Tenant legal support', percentage: 58 },
        { title: 'Monthly public progress reports', percentage: 49 },
      ],
      timeline: [28, 34, 41, 48, 57, 65],
      metrics: { voices: 129, countries: 8, consensus: 72, updateLabel: '8 hours ago' },
      geographic_insights: {
        key_insights: [
          'Irish diaspora contributors are connecting the issue to rising migration pressure across Europe.',
          'Local respondents want concrete delivery timelines more than symbolic statements.',
          'Commenters in France and Germany focus on shared urban rent pressure across capitals.',
        ],
        diaspora_perspectives: 'Diaspora responses emphasize the reputational cost of prolonged inaction more than short-term politics.',
        regional_patterns: 'Western European voices largely agree on transparency, legal aid, and temporary housing capacity as the first three moves.',
        active_countries: ['IE', 'GB', 'FR', 'DE', 'US'],
      },
    },
  },
  {
    id: 'demo-us-oversight',
    source: 'demo',
    title: 'Citywide march calls for independent oversight after arrest footage',
    description: 'Residents are demanding an external investigation and public release standards after footage of a violent arrest circulated overnight.',
    country_code: 'US',
    region: 'St. Louis, United States',
    source_type: 'verified media',
    status: 'active',
    opinions_count: 247,
    is_featured: true,
    created_at: '2026-05-29T10:00:00Z',
    analytics: {
      summary: 'Most voices want immediate independent oversight, officer suspension during review, and a public process that avoids selective evidence release.',
      emotional_landscape: { anger: 41, sadness: 16, solidarity: 18, fear: 12, hope: 13 },
      key_themes: [
        { title: 'External investigation', percentage: 81 },
        { title: 'Body-camera release rules', percentage: 66 },
        { title: 'Civilian review board powers', percentage: 55 },
      ],
      timeline: [44, 49, 56, 62, 70, 78],
      metrics: { voices: 247, countries: 14, consensus: 81, updateLabel: '12 hours ago' },
      geographic_insights: {
        key_insights: [
          'Participants in Canada and Ireland compare the case to wider police accountability debates.',
          'Local respondents prioritize evidence preservation and timeline guarantees.',
          'Diaspora communities focus on the emotional toll of recurring footage-driven crises.',
        ],
        diaspora_perspectives: 'Cross-border contributors are less interested in partisan framing and more focused on whether review systems can interrupt repeat harm.',
        regional_patterns: 'North American respondents strongly align around outside oversight and fast disclosure standards.',
        active_countries: ['US', 'CA', 'IE', 'GB', 'MX'],
      },
    },
  },
  {
    id: 'demo-nigeria-election',
    source: 'demo',
    title: 'Election transparency sit-in enters third day in Lagos',
    description: 'Civic monitors are asking the commission to publish regional discrepancy logs and open observer briefings.',
    country_code: 'NG',
    region: 'Lagos, Nigeria',
    source_type: 'civic monitor',
    status: 'active',
    opinions_count: 311,
    is_featured: true,
    created_at: '2026-05-27T20:00:00Z',
    analytics: {
      summary: 'Most participants support a calm, evidence-heavy approach centered on transparent discrepancy reporting, observer access, and judicial review.',
      emotional_landscape: { anger: 26, sadness: 9, solidarity: 31, fear: 14, hope: 20 },
      key_themes: [
        { title: 'Publish discrepancy logs', percentage: 83 },
        { title: 'Observer access to regional briefings', percentage: 64 },
        { title: 'Nonviolent accountability strategy', percentage: 57 },
      ],
      timeline: [38, 46, 52, 60, 69, 76],
      metrics: { voices: 311, countries: 15, consensus: 83, updateLabel: '2 days ago' },
      geographic_insights: {
        key_insights: [
          'Nigeria-based contributors want evidence handling rules to be visible in real time.',
          'Diaspora communities focus on institutional credibility more than any single result.',
          'Contributors across West Africa are emphasizing nonviolent pressure and independent observation.',
        ],
        diaspora_perspectives: 'Diaspora voices frame transparency as a long-run confidence issue that will shape turnout and trust beyond this cycle.',
        regional_patterns: 'Regional participants strongly align around public logs, observer access, and judicial pathways.',
        active_countries: ['NG', 'GH', 'GB', 'US'],
      },
    },
  },
  {
    id: 'demo-brazil-land',
    source: 'demo',
    title: 'Indigenous land-rights assembly expands along river corridor',
    description: 'A coalition of Indigenous leaders and environmental monitors is calling for enforcement against illegal clearing near protected territory.',
    country_code: 'BR',
    region: 'Manaus, Brazil',
    source_type: 'ngo network',
    status: 'active',
    opinions_count: 203,
    is_featured: false,
    created_at: '2026-05-28T15:00:00Z',
    analytics: {
      summary: 'The dominant demand is for enforceable land protection backed by satellite monitoring, legal aid, and federal transparency on investigations.',
      emotional_landscape: { anger: 27, sadness: 18, solidarity: 29, fear: 10, hope: 16 },
      key_themes: [
        { title: 'Protected area enforcement', percentage: 79 },
        { title: 'Legal aid for communities', percentage: 57 },
        { title: 'Transparent incident mapping', percentage: 46 },
      ],
      timeline: [30, 36, 40, 51, 60, 69],
      metrics: { voices: 203, countries: 11, consensus: 79, updateLabel: '1 day ago' },
      geographic_insights: {
        key_insights: [
          'Brazil-based contributors are asking for immediate enforcement rather than new pledges.',
          'European respondents focus on supply-chain accountability for land-linked commodities.',
          'Diaspora commentary emphasizes preservation of local leadership in any external response.',
        ],
        diaspora_perspectives: 'Contributors abroad want stronger international visibility but caution against solutions that sideline community governance.',
        regional_patterns: 'Latin American participants strongly prioritize land protection, evidence gathering, and long-term funding.',
        active_countries: ['BR', 'US', 'FR', 'DE'],
      },
    },
  },
  {
    id: 'demo-india-water',
    source: 'demo',
    title: 'Neighborhood councils demand emergency water schedule in Bengaluru',
    description: 'Residents say tanker delays and uneven municipal updates are leaving entire districts without reliable access during peak heat.',
    country_code: 'IN',
    region: 'Bengaluru, India',
    source_type: 'city dispatch',
    status: 'active',
    opinions_count: 221,
    is_featured: false,
    created_at: '2026-05-28T09:30:00Z',
    analytics: {
      summary: 'Voices are converging on public water schedules, district-level dashboards, and reserve delivery guarantees for hospitals and schools.',
      emotional_landscape: { anger: 24, sadness: 13, solidarity: 20, fear: 22, hope: 21 },
      key_themes: [
        { title: 'Public distribution schedule', percentage: 74 },
        { title: 'Priority access for schools and clinics', percentage: 61 },
        { title: 'District outage dashboard', percentage: 53 },
      ],
      timeline: [32, 39, 45, 49, 57, 66],
      metrics: { voices: 221, countries: 10, consensus: 74, updateLabel: '1 day ago' },
      geographic_insights: {
        key_insights: [
          'Local voices care most about predictability, not just supply volume.',
          'Contributors in Australia and Kenya compare the issue to other fast-growing cities under climate stress.',
          'Diaspora respondents want clearer public data to support community coordination.',
        ],
        diaspora_perspectives: 'Respondents abroad frame the crisis as an urban planning signal rather than a temporary disruption.',
        regional_patterns: 'South Asian and East African contributors both emphasize schedule transparency and priority protections.',
        active_countries: ['IN', 'AU', 'KE', 'US'],
      },
    },
  },
];

function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '';
  const codePoints = countryCode.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function getCountryName(code) {
  return COUNTRY_NAMES[code] || code;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function weeksTracked(createdAt) {
  return Math.max(1, Math.ceil((Date.now() - new Date(createdAt).getTime()) / (7 * 24 * 3600 * 1000)));
}

function buildTimeline(feed, createdAt) {
  const startMs = new Date(createdAt).getTime();
  const weeks = weeksTracked(createdAt);
  const bins = new Array(Math.min(weeks, 8)).fill(0);
  (feed || []).forEach(item => {
    const idx = Math.floor((new Date(item.created_at).getTime() - startMs) / (7 * 24 * 3600 * 1000));
    if (idx >= 0 && idx < bins.length) bins[idx]++;
  });
  const max = Math.max(...bins, 1);
  return bins.map(v => Math.round((v / max) * 100));
}

function activeCountriesFromFeed(feed, fallbackCountry) {
  if (!feed || feed.length === 0) return [fallbackCountry].filter(Boolean);
  const seen = new Set();
  feed.forEach(item => { if (item.origin) seen.add(item.origin); if (item.residence) seen.add(item.residence); });
  return seen.size > 0 ? [...seen] : [fallbackCountry].filter(Boolean);
}

function createFallbackAnalytics(event) {
  return {
    _no_synthesis: true,
    summary: '',
    emotional_landscape: null,
    key_themes: [],
    timeline: [],
    metrics: { voices: event.opinions_count || 0, countries: 0, consensus: 0, updateLabel: 'Awaiting synthesis' },
    geographic_insights: {
      key_insights: [],
      diaspora_perspectives: '',
      regional_patterns: '',
      active_countries: [event.country_code].filter(Boolean),
    },
  };
}

function makeDemoTopic(event) {
  return {
    id: null,
    title: event.title,
    description: event.description,
    country_code: event.country_code,
    affected_country_code: event.affected_country_code || null,
    region: event.region,
    source_type: event.source_type,
    opinions_count: event.opinions_count || 0,
    created_at: event.created_at,
    is_demo: true,
  };
}

function normalizeLiveTopic(topic) {
  const n = {
    id: `live-${topic.id}`,
    topicId: topic.id,
    source: 'live',
    title: topic.title,
    description: topic.description || 'Live event currently gathering anonymous perspectives.',
    country_code: topic.country_code || null,
    affected_country_code: topic.affected_country_code || null,
    region: topic.region || 'Global',
    source_type: topic.source_type || 'live topic',
    status: topic.status || 'active',
    opinions_count: topic.opinions_count || 0,
    is_featured: Boolean(topic.is_featured),
    image_url: topic.image_url || null,
    created_at: topic.created_at || new Date().toISOString(),
    rawTopic: topic,
  };
  return { ...n, analytics: createFallbackAnalytics(n) };
}

function mergeLiveSummary(event, summary, opinionFeed) {
  const feed = opinionFeed?.feed || [];
  const timeline = buildTimeline(feed, event.created_at);
  const active_countries = activeCountriesFromFeed(feed, event.country_code);
  const voices = opinionFeed?.total ?? event.opinions_count ?? 0;
  const countries = opinionFeed?.countries ?? 0;
  return {
    summary: summary.summary || '',
    emotional_landscape: summary.emotional_landscape || null,
    key_themes: summary.key_themes || [],
    timeline,
    metrics: {
      voices,
      countries,
      consensus: summary.key_themes?.[0]?.percentage ?? 0,
      updateLabel: summary.updated_at ? timeAgo(summary.updated_at) : 'Awaiting synthesis',
    },
    geographic_insights: {
      key_insights: summary.geographic_insights?.key_insights || [],
      diaspora_perspectives: summary.geographic_insights?.diaspora_perspectives || '',
      regional_patterns: summary.geographic_insights?.regional_patterns || '',
      active_countries,
    },
  };
}

const EMOTION_COLORS = {
  anger: '#f4212e',
  sadness: '#1d9bf0',
  solidarity: '#00ba7c',
  fear: '#ff7a00',
  hope: '#7856ff',
};

const EMOTION_EMOJIS = {
  anger: '😠', sadness: '😢', solidarity: '🤝', fear: '😨', hope: '🌟',
};

const EMOTION_FEED_META = {
  anger:      { emoji: '😠', color: '#f4212e' },
  sadness:    { emoji: '😢', color: '#1d9bf0' },
  solidarity: { emoji: '🤝', color: '#00ba7c' },
  fear:       { emoji: '😨', color: '#ff7a00' },
  hope:       { emoji: '🌟', color: '#7856ff' },
  neutral:    { emoji: '💬', color: '#6b7280' },
};

function DashboardPage({ user, topics, onSelectTopic }) {
  const voices = useLiveVoices();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeEventId, setActiveEventId] = useState(null);
  const liveTopicsSeeded = useRef(false);
  const [liveSummary, setLiveSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [opinionFeed, setOpinionFeed] = useState(null);
  const [viewpointClusters, setViewpointClusters] = useState(null);
  const [mobileTab, setMobileTab] = useState('feed');

  const allEvents = useMemo(() => {
    const liveEvents = topics.map(normalizeLiveTopic);
    const usedTitles = new Set(liveEvents.map(e => e.title.toLowerCase()));
    return [
      ...liveEvents,
      ...DEMO_EVENTS.filter(e => !usedTitles.has(e.title.toLowerCase())),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [topics]);

  const filteredEvents = useMemo(() => allEvents.filter(event => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      event.title.toLowerCase().includes(q) ||
      event.region.toLowerCase().includes(q) ||
      (event.description || '').toLowerCase().includes(q);
    const matchFilter = filter === 'all' || filter === event.status ||
      (filter === 'featured' && event.is_featured);
    const matchCountry = !countryFilter ||
      event.country_code === countryFilter ||
      event.affected_country_code === countryFilter;
    const matchCategory = !categoryFilter || event.source_type === categoryFilter;
    return matchSearch && matchFilter && matchCountry && matchCategory;
  }), [allEvents, filter, searchTerm, countryFilter, categoryFilter]);

  // When live topics arrive for the first time, jump to the most recent one.
  useEffect(() => {
    if (topics.length > 0 && !liveTopicsSeeded.current) {
      liveTopicsSeeded.current = true;
      // allEvents is sorted newest-first; live events are at the top when present
      const mostRecent = allEvents[0];
      if (mostRecent) setActiveEventId(mostRecent.id);
    }
  }, [topics, allEvents]); // eslint-disable-line

  useEffect(() => {
    if (filteredEvents.length === 0) { setActiveEventId(null); return; }
    const has = filteredEvents.some(e => e.id === activeEventId);
    if (!has) setActiveEventId(filteredEvents[0].id);
  }, [activeEventId, filteredEvents]);

  const activeEvent = filteredEvents.find(e => e.id === activeEventId) ||
    allEvents.find(e => e.id === activeEventId) || filteredEvents[0] || allEvents[0] || null;

  useEffect(() => {
    let ignore = false;
    if (!activeEvent || activeEvent.source !== 'live') {
      setLiveSummary(null);
      setOpinionFeed(null);
      setViewpointClusters(null);
      setSummaryLoading(false);
      return;
    }
    setSummaryLoading(true);
    setLiveSummary(null);
    setOpinionFeed(null);
    setViewpointClusters(null);
    axios.get(`${API_BASE}/topics/${activeEvent.topicId}/summary`)
      .then(r => { if (!ignore) setLiveSummary(r.data); })
      .catch(() => { if (!ignore) setLiveSummary(null); })
      .finally(() => { if (!ignore) setSummaryLoading(false); });
    axios.get(`${API_BASE}/topics/${activeEvent.topicId}/opinion-feed`)
      .then(r => { if (!ignore) setOpinionFeed(r.data); })
      .catch(() => { if (!ignore) setOpinionFeed(null); });
    axios.get(`${API_BASE}/topics/${activeEvent.topicId}/viewpoints`)
      .then(r => { if (!ignore) setViewpointClusters(r.data); })
      .catch(() => { if (!ignore) setViewpointClusters(null); });
    return () => { ignore = true; };
  }, [activeEvent]);

  const activeAnalytics = useMemo(() => {
    if (!activeEvent) return null;
    if (activeEvent.source === 'live') return mergeLiveSummary(activeEvent, liveSummary || {}, opinionFeed);
    return activeEvent.analytics;
  }, [activeEvent, liveSummary, opinionFeed]);

  // Unique countries and categories for filter dropdowns — must be before early return
  const filterCountries = useMemo(() => {
    const codes = [...new Set(
      allEvents.flatMap(e => [e.country_code, e.affected_country_code].filter(Boolean))
    )].sort((a, b) => (COUNTRY_NAMES[a] || a).localeCompare(COUNTRY_NAMES[b] || b));
    return codes;
  }, [allEvents]);

  const filterCategories = useMemo(() => {
    return [...new Set(allEvents.map(e => e.source_type).filter(Boolean))].sort();
  }, [allEvents]);

  if (!activeEvent || !activeAnalytics) return null;

  const activeCountries = activeAnalytics.geographic_insights.active_countries || [];
  // All unique event countries — shown as overview markers on the map
  const allEventCountries = [...new Set(
    allEvents.flatMap(e => [e.country_code, e.affected_country_code].filter(Boolean))
  )];

  return (
    <div className="dashboard">
      <div className="dashboard-mobile-tabs">
        <button
          className={`dmt-tab ${mobileTab === 'feed' ? 'active' : ''}`}
          onClick={() => setMobileTab('feed')}
        >
          Events
        </button>
        <button
          className={`dmt-tab ${mobileTab === 'details' ? 'active' : ''}`}
          onClick={() => setMobileTab('details')}
        >
          Analytics
        </button>
      </div>
      <div className={`dashboard-shell ${mobileTab === 'details' ? 'mobile-show-details' : ''}`}>

        {/* ===== Left Rail: Feed ===== */}
        <section className="dashboard-rail">
          <header className="rail-header">
            <h1>Global Civic Feed</h1>
            {user && (
              <div className="user-profile-badge">
                <div className="profile-id">
                  <span className="profile-label">Your anonymous ID</span>
                  <span className="profile-value">{user.user_id}</span>
                </div>
                <div className="profile-flags">
                  <span title="Origin"><FlagImg code={user.country_origin} size={20} /></span>
                  <span className="flag-arrow">→</span>
                  <span title="Residence"><FlagImg code={user.country_residence} size={20} /></span>
                </div>
              </div>
            )}
          </header>

          {/* Live platform stat bar */}
          <div className="live-stat-bar">
            <div className="lsb-item">
              <span className="lsb-pulse" />
              <span className="lsb-value">{fmtM(voices)}</span>
              <span className="lsb-label">voices worldwide</span>
            </div>
            <div className="lsb-sep" />
            <div className="lsb-item">
              <span className="lsb-value">190+</span>
              <span className="lsb-label">countries</span>
            </div>
            <div className="lsb-sep" />
            <div className="lsb-item">
              <span className="lsb-value">{allEvents.length}</span>
              <span className="lsb-label">active topics</span>
            </div>
          </div>

          <div className="feed-controls">
            <div className="search-wrap">
              <svg className="search-icon" viewBox="0 0 20 20" fill="none">
                <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                className="search-input"
                type="text"
                placeholder="Search by title, region, or keyword..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="search-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">×</button>
              )}
            </div>
            <div className="filter-tabs">
              {['all', 'featured', 'active'].map(f => (
                <button
                  key={f}
                  className={`filter-tab ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : f === 'featured' ? 'Featured' : 'Active'}
                </button>
              ))}
            </div>

            {/* Country + category dropdowns */}
            <div className="filter-dropdowns">
              <select
                className="filter-select"
                value={countryFilter}
                onChange={e => setCountryFilter(e.target.value)}
                aria-label="Filter by country"
              >
                <option value="">All countries</option>
                {filterCountries.map(code => (
                  <option key={code} value={code}>{getCountryFlag(code)} {getCountryName(code)}</option>
                ))}
              </select>
              <select
                className="filter-select"
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">All categories</option>
                {filterCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Active filter badge */}
            {(countryFilter || categoryFilter) && (
              <div className="active-filters">
                {countryFilter && (
                  <span className="filter-badge">
                    {getCountryFlag(countryFilter)} {getCountryName(countryFilter)}
                    <button onClick={() => setCountryFilter('')} aria-label="Remove country filter">×</button>
                  </span>
                )}
                {categoryFilter && (
                  <span className="filter-badge">
                    {categoryFilter}
                    <button onClick={() => setCategoryFilter('')} aria-label="Remove category filter">×</button>
                  </span>
                )}
                <button className="filter-clear-all" onClick={() => { setCountryFilter(''); setCategoryFilter(''); setSearchTerm(''); setFilter('all'); }}>
                  Clear all
                </button>
              </div>
            )}
          </div>

          <div className="event-feed">
            {filteredEvents.map(event => (
              <div
                key={event.id}
                className={`event-card ${activeEvent.id === event.id ? 'selected' : ''}`}
                onClick={() => { setActiveEventId(event.id); if (window.innerWidth <= 860) setMobileTab('details'); }}
              >
                <div className="event-card-inner">
                  <div className="event-flag">
                    {event.affected_country_code && event.affected_country_code !== event.country_code ? (
                      <span className="dual-flag">
                        <span className="flag-incident" title={`Incident in ${getCountryName(event.country_code)}`}><FlagImg code={event.country_code} size={22} /></span>
                        <span className="flag-vs">vs</span>
                        <span className="flag-affected" title={`Affecting people from ${getCountryName(event.affected_country_code)}`}><FlagImg code={event.affected_country_code} size={22} /></span>
                      </span>
                    ) : (
                      <FlagImg code={event.country_code} size={28} />
                    )}
                  </div>
                  <div className="event-body">
                    <div className="event-meta-top">
                      <span className="event-region">{event.region}</span>
                      <span className="event-age">{timeAgo(event.created_at)}</span>
                    </div>
                    <h3 className="event-title">{event.title}</h3>
                    <p className="event-desc">{event.description}</p>
                    <div className="event-stats">
                      <span className="stat-pill">
                        <IconMic size={13} />
                        {event.opinions_count} voices
                      </span>
                      <span className="source-tag">{event.source_type}</span>
                      {event.is_featured && <span className="featured-tag">Featured</span>}
                      {event.source === 'live' && <span className="live-tag">Live</span>}
                    </div>
                    {onSelectTopic && (
                      <button
                        className="card-participate-btn"
                        onClick={e => {
                          e.stopPropagation();
                          onSelectTopic(event.source === 'live' ? event.rawTopic : makeDemoTopic(event));
                        }}
                      >
                        Share your voice →
                      </button>
                    )}
                  </div>
                  {event.image_url && (
                    <img
                      src={event.image_url}
                      alt=""
                      className="event-thumb"
                      loading="lazy"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== Right Panel: Analytics ===== */}
        <section className="analytics-panel">
          <div className="panel-event-header">
            <div className="panel-location">
              {activeEvent.affected_country_code && activeEvent.affected_country_code !== activeEvent.country_code ? (
                <span className="panel-dual-flag">
                  <span title={`Incident in ${getCountryName(activeEvent.country_code)}`}><FlagImg code={activeEvent.country_code} size={26} /></span>
                  <span className="panel-flag-arrow">→</span>
                  <span title={`Affecting people from ${getCountryName(activeEvent.affected_country_code)}`}><FlagImg code={activeEvent.affected_country_code} size={26} /></span>
                </span>
              ) : (
                <span className="panel-flag"><FlagImg code={activeEvent.country_code} size={26} /></span>
              )}
              <div>
                <span className="panel-region">{activeEvent.region}</span>
                {activeEvent.affected_country_code && activeEvent.affected_country_code !== activeEvent.country_code && (
                  <span className="panel-affected-label">
                    Affecting: <FlagImg code={activeEvent.affected_country_code} size={16} style={{ marginRight: 3 }} /> {getCountryName(activeEvent.affected_country_code)} nationals
                  </span>
                )}
                <span className="panel-updated">{activeAnalytics.metrics.updateLabel}</span>
              </div>
            </div>
            <div className="panel-actions">
              {onSelectTopic && (
                <button
                  className="btn-open-topic"
                  onClick={() => onSelectTopic(
                    activeEvent.source === 'live' ? activeEvent.rawTopic : makeDemoTopic(activeEvent)
                  )}
                >
                  Participate →
                </button>
              )}
            </div>
          </div>

          <h2 className="panel-title">{activeEvent.title}</h2>
          <p className="panel-summary">{activeAnalytics.summary}</p>

          <div className="analytics-grid">

            {/* OSM Map */}
            <article className="analytics-card map-card">
              <div className="card-header">
                <span className="card-label">Live map · OpenStreetMap</span>
                <div className="map-legend">
                  <span><i className="leg-dot leg-focus"/>Incident</span>
                  {activeEvent.affected_country_code && activeEvent.affected_country_code !== activeEvent.country_code && (
                    <span><i className="leg-dot leg-affected"/>Affected</span>
                  )}
                  <span><i className="leg-dot leg-active"/>Responding</span>
                  <span><i className="leg-dot leg-events"/>Other events</span>
                </div>
              </div>
              <div className="map-container">
                <MapView
                  focusCountry={activeEvent.country_code}
                  affectedCountry={activeEvent.affected_country_code}
                  activeCountries={activeCountries}
                  allEventCountries={allEventCountries}
                  onCountryClick={code => {
                    setCountryFilter(prev => prev === code ? '' : code);
                    if (window.innerWidth <= 860) setMobileTab('feed');
                  }}
                />
              </div>
              {countryFilter && (
                <div className="map-filter-hint">
                  Showing events for <strong>{getCountryFlag(countryFilter)} {getCountryName(countryFilter)}</strong>
                  <button onClick={() => setCountryFilter('')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                    Clear ×
                  </button>
                </div>
              )}
              <div className="country-pills">
                {activeCountries.map(code => {
                  const isIncident = code === activeEvent.country_code;
                  const isAffected = activeEvent.affected_country_code && code === activeEvent.affected_country_code;
                  return (
                    <span
                      key={code}
                      className={`country-pill${isIncident ? ' pill-incident' : isAffected ? ' pill-affected' : ''}`}
                    >
                      <FlagImg code={code} size={16} style={{ marginRight: 4 }} /> {getCountryName(code)}
                    </span>
                  );
                })}
              </div>
            </article>

            {/* Pulse Metrics */}
            <article className="analytics-card metrics-card">
              <div className="card-header">
                <span className="card-label">Pulse</span>
              </div>
              <div className="metrics-grid">
                <div className="metric-box">
                  <span className="metric-number">{activeAnalytics.metrics.voices.toLocaleString()}</span>
                  <span className="metric-label">Voices</span>
                </div>
                <div className="metric-box">
                  <span className="metric-number">{activeAnalytics.metrics.countries || '—'}</span>
                  <span className="metric-label">Countries</span>
                </div>
                <div className="metric-box">
                  <span className="metric-number">{activeAnalytics.metrics.consensus ? `${activeAnalytics.metrics.consensus}%` : '—'}</span>
                  <span className="metric-label">Top consensus</span>
                </div>
                <div className="metric-box">
                  <span className="metric-number">{activeEvent.source === 'live' ? weeksTracked(activeEvent.created_at) : activeAnalytics.timeline.length}</span>
                  <span className="metric-label">Weeks tracked</span>
                </div>
              </div>
            </article>

            {/* Emotional Landscape */}
            <article className="analytics-card emotions-card">
              <div className="card-header">
                <span className="card-label">Emotional landscape</span>
              </div>
              {activeAnalytics.emotional_landscape ? (
                <div className="emotion-bars">
                  {Object.entries(activeAnalytics.emotional_landscape).map(([label, val]) => (
                    <div key={label} className="emotion-row">
                      <div className="emotion-left">
                        <span className="emotion-emoji">{EMOTION_EMOJIS[label] || '●'}</span>
                        <span className="emotion-label">{label}</span>
                      </div>
                      <div className="emotion-track">
                        <div
                          className="emotion-fill"
                          style={{ width: `${val}%`, background: EMOTION_COLORS[label] || 'var(--accent)' }}
                        />
                      </div>
                      <span className="emotion-pct">{val}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="card-pending">Emotional breakdown will appear after AI synthesis is run.</p>
              )}
            </article>

            {/* Key Themes */}
            <article className="analytics-card themes-card">
              <div className="card-header">
                <span className="card-label">Key themes</span>
              </div>
              {activeAnalytics.key_themes.length > 0 ? (
                <div className="theme-rows">
                  {activeAnalytics.key_themes.map((theme, i) => (
                    <div key={theme.title} className="theme-row">
                      <div className="theme-rank">#{i + 1}</div>
                      <div className="theme-body">
                        <div className="theme-top">
                          <span className="theme-title">{theme.title}</span>
                          <span className="theme-pct">{theme.percentage}%</span>
                        </div>
                        <div className="theme-track">
                          <div className="theme-fill" style={{ width: `${theme.percentage}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="card-pending">Key themes will appear after AI synthesis is run on this topic.</p>
              )}
            </article>

            {/* Timeline */}
            <article className="analytics-card timeline-card">
              <div className="card-header">
                <span className="card-label">Attention timeline</span>
              </div>
              {activeAnalytics.timeline.length > 0 ? (
                <div className="timeline-bars">
                  {activeAnalytics.timeline.map((val, i) => (
                    <div key={i} className="tl-col">
                      <div className="tl-track">
                        <div className="tl-bar" style={{ height: `${val || 2}%` }} />
                      </div>
                      <span className="tl-label">W{i + 1}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="card-pending">Opinion activity by week will appear as voices are added.</p>
              )}
            </article>

            {/* Geographic Insights */}
            <article className="analytics-card geo-card">
              <div className="card-header">
                <span className="card-label">Geographic insight</span>
              </div>
              {activeAnalytics.geographic_insights.key_insights.length > 0 ? (
                <>
                  <ul className="geo-insights">
                    {activeAnalytics.geographic_insights.key_insights.map(insight => (
                      <li key={insight}>{insight}</li>
                    ))}
                  </ul>
                  <div className="geo-notes">
                    {activeAnalytics.geographic_insights.diaspora_perspectives && (
                      <div className="geo-note">
                        <span className="note-label">Diaspora perspectives</span>
                        <p>{activeAnalytics.geographic_insights.diaspora_perspectives}</p>
                      </div>
                    )}
                    {activeAnalytics.geographic_insights.regional_patterns && (
                      <div className="geo-note">
                        <span className="note-label">Regional patterns</span>
                        <p>{activeAnalytics.geographic_insights.regional_patterns}</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="card-pending">Geographic patterns across diaspora and resident voices will appear after synthesis.</p>
              )}
            </article>

            {/* Opinion Feed */}
            {opinionFeed && opinionFeed.total > 0 && (
              <article className="analytics-card voices-card">
                <div className="card-header">
                  <span className="card-label">Voices on this topic</span>
                  <div className="voices-meta">
                    <span className="voices-count">{opinionFeed.total} voices · {opinionFeed.countries} countries</span>
                  </div>
                </div>

                <div className="voices-breakdown">
                  {opinionFeed.local_count > 0 && (
                    <span className="vb-pill vb-local">{opinionFeed.local_count} in-country</span>
                  )}
                  {opinionFeed.diaspora_count > 0 && (
                    <span className="vb-pill vb-diaspora">{opinionFeed.diaspora_count} diaspora</span>
                  )}
                  {opinionFeed.intl_count > 0 && (
                    <span className="vb-pill vb-intl">{opinionFeed.intl_count} international</span>
                  )}
                </div>

                <div className="voices-feed">
                  {opinionFeed.feed.map((item, i) => {
                    const em = EMOTION_FEED_META[item.emotion] || EMOTION_FEED_META.neutral;
                    const flag = code => !code || code.length !== 2 ? '' :
                      String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
                    const sameCountry = item.origin === item.residence;
                    const diff = Date.now() - new Date(item.created_at).getTime();
                    const mins = Math.floor(diff / 60000);
                    const ago = mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins/60)}h ago` : `${Math.floor(mins/1440)}d ago`;
                    return (
                      <div key={i} className="vf-item">
                        <span className="vf-emotion" style={{ color: em.color }}>{em.emoji}</span>
                        <div className="vf-body">
                          <p className="vf-phrase">"{item.key_phrase}"</p>
                          <div className="vf-meta">
                            <span className="vf-flags">
                              {flag(item.origin)}
                              {!sameCountry && <><span className="vf-arrow">→</span>{flag(item.residence)}</>}
                              {item.is_diaspora && <span className="vf-tag vf-tag--diaspora">diaspora</span>}
                              {item.is_local && <span className="vf-tag vf-tag--local">local</span>}
                            </span>
                            <span className="vf-time">{ago}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button className="btn-participate" onClick={() => onSelectTopic(activeEvent.rawTopic)}>
                  Add your voice →
                </button>
              </article>
            )}

            {/* Viewpoint Clusters */}
            {viewpointClusters && viewpointClusters.clusters && viewpointClusters.clusters.length > 0 && (
              <article className="analytics-card clusters-card">
                <div className="card-header">
                  <span className="card-label">Opinion groups</span>
                  <span className="card-sublabel">Similar views clustered by AI · {viewpointClusters.total_opinions} voices analysed</span>
                </div>

                <div className="vc-list">
                  {viewpointClusters.clusters.map((cl, i) => {
                    const flag = code => !code || code.length !== 2 ? '' :
                      String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
                    const stanceColors = {
                      critical:   { color: '#dc2626', bg: '#fee2e2' },
                      demanding:  { color: '#d97706', bg: '#fef3c7' },
                      skeptical:  { color: '#7c3aed', bg: '#ede9fe' },
                      supportive: { color: '#059669', bg: '#d1fae5' },
                      reformist:  { color: '#2563eb', bg: '#dbeafe' },
                      neutral:    { color: '#6b7280', bg: '#f3f4f6' },
                    };
                    const sc = stanceColors[cl.stance] || stanceColors.neutral;
                    return (
                      <div key={i} className="vc-cluster">
                        <div className="vc-cluster-top">
                          <div className="vc-cluster-left">
                            <span className="vc-label">{cl.label}</span>
                            <span className="vc-stance" style={{ color: sc.color, background: sc.bg }}>{cl.stance}</span>
                          </div>
                          <span className="vc-count">{cl.count} {cl.count === 1 ? 'voice' : 'voices'}</span>
                        </div>

                        <div className="vc-bar-row">
                          <div className="vc-bar-track">
                            <div className="vc-bar-fill" style={{ width: `${cl.percentage}%` }} />
                          </div>
                          <span className="vc-pct">{cl.percentage}%</span>
                        </div>

                        {cl.argument_core && (
                          <p className="vc-argument">{cl.argument_core}</p>
                        )}

                        {cl.representative_quote && (
                          <blockquote className="vc-quote">"{cl.representative_quote}"</blockquote>
                        )}

                        {/* Geographic breakdown */}
                        {(cl.origin_countries?.length > 0 || cl.residence_countries?.length > 0) && (
                          <div className="vc-geo">
                            {cl.origin_countries?.length > 0 && (
                              <div className="vc-geo-row">
                                <span className="vc-geo-label">Origins:</span>
                                <span className="vc-geo-flags">
                                  {cl.origin_countries.map(({ code, count }) => (
                                    <span key={code} className="vc-geo-flag" title={`${code}: ${count} voice${count !== 1 ? 's' : ''}`}>
                                      {flag(code)}<sup>{count}</sup>
                                    </span>
                                  ))}
                                </span>
                              </div>
                            )}
                            {cl.residence_countries?.length > 0 && (
                              <div className="vc-geo-row">
                                <span className="vc-geo-label">Living in:</span>
                                <span className="vc-geo-flags">
                                  {cl.residence_countries.map(({ code, count }) => (
                                    <span key={code} className="vc-geo-flag" title={`${code}: ${count} voice${count !== 1 ? 's' : ''}`}>
                                      {flag(code)}<sup>{count}</sup>
                                    </span>
                                  ))}
                                </span>
                              </div>
                            )}
                            {(cl.diaspora_in_cluster > 0 || cl.incountry_in_cluster > 0) && (
                              <div className="vc-geo-row vc-geo-tags">
                                {cl.diaspora_in_cluster > 0 && (
                                  <span className="vc-tag vc-tag--diaspora">{cl.diaspora_in_cluster} diaspora</span>
                                )}
                                {cl.incountry_in_cluster > 0 && (
                                  <span className="vc-tag vc-tag--local">{cl.incountry_in_cluster} local</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </article>
            )}

            {/* Prompt to participate when no voices yet */}
            {activeEvent.source === 'live' && (!opinionFeed || opinionFeed.total === 0) && (
              <article className="analytics-card voices-card voices-card--empty">
                <div className="card-header">
                  <span className="card-label">Voices on this topic</span>
                </div>
                <p className="voices-empty-text">No voices yet. Be the first to speak on this topic.</p>
                <button className="btn-participate" onClick={() => onSelectTopic(activeEvent.rawTopic)}>
                  Share your opinion →
                </button>
              </article>
            )}

          </div>

          {summaryLoading && (
            <div className="synthesis-loading">
              Loading live synthesis…
            </div>
          )}

          {/* How it works */}
          <div className="how-it-works">
            <h3>How Proteste works</h3>
            <div className="hiw-grid">
              {[
                { Icon: IconLock, title: 'Privacy first', text: 'Your opinion stays anonymous. Only AI summaries are public — never raw text.' },
                { Icon: IconSparkles, title: 'AI synthesis', text: 'Claude clusters themes, detects sentiment, and generates neutral public summaries.' },
                { Icon: IconGlobe, title: 'Global voice', text: 'Origin & residence patterns show how perspective changes across borders.' },
                { Icon: IconBarChart, title: 'Clear results', text: 'Charts, timelines, and maps make public sentiment easier to understand.' },
              ].map(({ Icon, title, text }) => (
                <div key={title} className="hiw-item">
                  <span className="hiw-icon"><Icon size={22} /></span>
                  <h4>{title}</h4>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

export default DashboardPage;
